/**
 * `useAltivMutation` — the canonical mutation hook for Altiv.
 *
 * Wraps TanStack `useMutation` so every save / update / delete in the app
 * follows the same pattern:
 *
 *   - one `mutate` / `mutateAsync` entry point
 *   - automatic invalidation of one or more query-key roots on success
 *   - uniform `isPending` / `error` state for loading + error UI
 *   - optional optimistic update (rolled back on error)
 *   - centralised error reporting (Sentry + admin_alerts) when configured
 *
 * Per Sprint 2.3 conventions (aviation context):
 *   - Optimistic is OPT-IN, not the default. Use it only for fast UI affordances
 *     where eventual consistency is fine (chat send, mute toggles, notification
 *     prefs). Anything safety-critical (trip create, expense, fuel order, leg
 *     edit, signature) stays pessimistic — show "Guardando..." and confirm.
 *
 *   - `invalidates` is an array of query-key roots. Each entry is invalidated
 *     after success via prefix-match on the first key element, mirroring the
 *     legacy `invalidateByPrefix` semantics. Example:
 *
 *       useAltivMutation({
 *         mutationFn: (body) => api.post("/expenses", body),
 *         invalidates: ["/expenses", "/dashboard"],
 *       });
 *
 *   - Returns the TanStack mutation object verbatim (no surface translation)
 *     so consumers have full API access (`reset`, `status`, `variables`, etc.).
 */

import { useMutation, useQueryClient, type UseMutationOptions } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { reportClientError } from "@/lib/errorReporter";

export type AltivMutationOptions<TData, TVariables, TContext = unknown> = Omit<
  UseMutationOptions<TData, unknown, TVariables, TContext>,
  "onSuccess" | "onError"
> & {
  /** Query-key roots to invalidate (prefix-match) after a successful mutation. */
  invalidates?: string[];
  /** Optional pre-mutation hook for optimistic updates. Return rollback context. */
  optimistic?: (_variables: TVariables) => TContext | void;
  /** Optional rollback handler invoked on error with the context from `optimistic`. */
  rollback?: (_variables: TVariables, _context: TContext | undefined) => void;
  /** Optional onSuccess (runs AFTER invalidations). */
  onSuccess?: (_data: TData, _variables: TVariables, _context: TContext | undefined) => void;
  /** Optional onError (runs AFTER rollback). */
  onError?: (_error: unknown, _variables: TVariables, _context: TContext | undefined) => void;
  /**
   * If true, report errors to Sentry + admin_alerts. Default true.
   * Set false for expected-fail flows (e.g. retryable network blip in chat).
   */
  reportErrors?: boolean;
};

export function useAltivMutation<TData = unknown, TVariables = void, TContext = unknown>(
  opts: AltivMutationOptions<TData, TVariables, TContext>
) {
  const {
    invalidates,
    optimistic,
    rollback,
    onSuccess,
    onError,
    reportErrors = true,
    ...rest
  } = opts;
  const queryClient = useQueryClient();

  return useMutation<TData, unknown, TVariables, TContext>({
    ...rest,
    onMutate: optimistic
      ? (variables: TVariables) => {
          const ctx = optimistic(variables);
          return ctx as TContext;
        }
      : undefined,
    onSuccess: (data, variables, context) => {
      if (invalidates && invalidates.length > 0) {
        for (const prefix of invalidates) {
          queryClient.invalidateQueries({
            predicate: (query) =>
              typeof query.queryKey[0] === "string" &&
              (query.queryKey[0] as string).startsWith(prefix),
          });
        }
      }
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (rollback) rollback(variables, context);
      if (reportErrors) {
        // El detalle del backend va PRIMERO a propósito. Axios siempre llena
        // `error.message` con "Request failed with status code 400", así que
        // leerlo antes cortocircuitaba el `||` y el reporte nunca guardaba el
        // motivo real — un 400 de /calendar quedó indiagnosticable (2026-07-19).
        const res = (
          error as {
            response?: {
              status?: number;
              data?: { message?: string; detail?: string; code?: string; field?: string };
            };
          }
        )?.response;
        const data = res?.data;
        const message =
          data?.message ||
          data?.detail ||
          (error as { message?: string })?.message ||
          "Mutation failed";
        reportClientError({
          kind: "frontend_mutation_error",
          severity: "warn",
          message,
          stack: (error as { stack?: string })?.stack || "",
          extra: {
            status: res?.status,
            code: data?.code,
            field: data?.field,
            axiosMessage: (error as { message?: string })?.message,
          },
        });
        Sentry.captureException(error, { tags: { boundary: "mutation" } });
      }
      onError?.(error, variables, context);
    },
  });
}
